/**
 * an `Array.sort` compatible predicate function to sort an array ascending
 *
 * @param key if {@link a} and {@link b} are objects, you can sort by a key of their type T
 * @example
 * const records: { name: string, id: number }[] = [ ... ]
 * records.sort(alphabeticalSort('name'))
 * records.sort(alphabeticalSort('id'))
 *
 * const strings: string[] = [ ... ]
 * strings.sort(alphabeticalSort())
 *
 * const numbers: number[] = [ ... ]
 * numbers.sort(alphabeticalSort())
 *
 * const dates: Date[] = [ ... ]
 * dates.sort(alphabeticalSort())
 *
 * @remarks
 * `Array.prototype.sort` moves top-level `undefined` elements to the end without
 * calling its comparator. This function can therefore place nullish object
 * properties first, but it cannot place top-level `undefined` elements first.
 */
export function alphabeticalSort<T extends object>(key?: keyof T) {
  return (
    a: T | number | string | Date | undefined | null,
    b: T | number | string | Date | undefined | null
  ) => alphabeticalCompare(a, b, key)
}

/**
 * a generic sorting compare method to prioritize ascending over descending
 *
 * most of the time you can use {@link alphabeticalSort}, but you can use this
 * if you want more custom logic
 *
 * alphabeticalSort calls this function
 *
 * @param a the first element
 * @param b the second element
 * @param key an optional keyof T
 * @see {@link alphabeticalSort}
 * @example
 * const values = [ 'one', 'two', 'three' ]
 * values.sort((a, b) => alphabeticalCompare(a.slice(1), b.slice(1)))
 *
 * const records: {
 *  name: string,
 *  id: number,
 *  type: { name: string, id: number, subtype: { name: string, id: number } }
 * }[] = [ ... ]
 * values.sort((a, b) => alphabeticalCompare(a.type.subtype, b.type.subtype, 'name'))
 */
export function alphabeticalCompare<T extends object>(
  a: T | number | string | Date | undefined | null,
  b: T | number | string | Date | undefined | null,
  key?: keyof T
) {
  if (isNullish(a)) {
    if (isNullish(b)) return 0
    if (isSimple(b)) return -1
    else if (!key) throw new CannotSortError()
    const B = b[key]
    if (isNullish(B)) return 0
    if (isSimple(B)) return -1
    return 1 // same as a[key] > b[key]
  } else if (isNullish(b)) {
    if (isSimple(a)) return 1
    else if (!key) throw new CannotSortError()
    const A = a[key]
    if (isNullish(A)) return 0
    if (isSimple(A)) return 1
    return -1 // same as a[key] < b[key]
  } else if (isSimple(a)) {
    if (isString(a) && isString(b)) return simple(a, b)
    else if (isNumber(a) && isNumber(b)) return simple(a, b)
    else if (isDate(a) && isDate(b)) return simple(a, b)
    throw new MismatchError({ a, b })
  } else if (isSimple(b)) {
    if (isString(a) && isString(b)) return simple(a, b)
    else if (isNumber(a) && isNumber(b)) return simple(a, b)
    else if (isDate(a) && isDate(b)) return simple(a, b)
    throw new MismatchError({ a, b })
  } else if (!key) throw new CannotSortError()
  const A = a[key]
  const B = b[key]
  if (isNullish(A) && isNullish(B)) return 0
  else if (isNullish(B)) {
    if (isSimple(A)) return 1
    return -1 // same as a[key] < b[key]
  } else if (isNullish(A)) {
    if (isSimple(B)) return -1
    return 1 // same as a[key] > b[key]
  } else if (isSimple(A)) {
    if (isString(A) && isString(B)) return simple(A, B)
    else if (isNumber(A) && isNumber(B)) return simple(A, B)
    else if (isDate(A) && isDate(B)) return simple(A, B)
    else throw new MismatchError({ a: A, b: B })
  } else if (isSimple(B)) {
    if (isString(A) && isString(B)) return simple(A, B)
    else if (isNumber(A) && isNumber(B)) return simple(A, B)
    else if (isDate(A) && isDate(B)) return simple(A, B)
    else throw new MismatchError({ a: A, b: B })
  }
  return compare(A, B)
}

function simple<T extends string | number | Date>(a: T, b: T) {
  const lower = (value: string | number | Date) =>
    isString(value) ? value.toLowerCase() : value
  const A = lower(a)
  const B = lower(b)
  return compare(A, B)
}

/**
 * `a` and `b` are any on purpose because i can't be bothered to figure out how
 * to resolve `Date` and `NonNullable<T[keyof T]>` with each other
 *
 * by the time this is called they should be separated and if there's any
 * leakage, we'll just let javascript do the sorting
 */
function compare(a: any, b: any) {
  return a > b ? 1 : a < b ? -1 : 0
}

class CannotSortError extends Error {
  constructor() {
    super('Cannot sort elements of type T unless a key is provided')
  }
}

class MismatchError extends Error {
  constructor(options: { a: unknown; b: unknown }) {
    super(`Cannot compare a(${options.a}) and b(${options.b})`)
  }
}

/**
 * type guards can be dangerous as they promote a false sense of security
 *
 * however... with how much i'd have to write
 * `typeof a === 'string' && typeof b === 'string'`... i'll take that risk
 */

/** */
function isString(value: unknown): value is string {
  return typeof value === 'string'
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number'
}

function isDate(value: unknown): value is Date {
  return value instanceof Date
}

function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

function isSimple(value: unknown): value is string | number | Date {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date
  )
}
